import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'

import {
  ArtistAddCollectionsToEventMutation,
  ArtistAddCollectionsToEventMutationVariables,
  ArtistDeleteEventMutation,
  ArtistDeleteEventMutationVariables,
  ArtistRemoveCollectionFromEventMutation,
  ArtistRemoveCollectionFromEventMutationVariables,
  ArtistUpdateEvent,
  ArtistUpdateEventMutation,
  ArtistUpdateEventMutationVariables,
  ArtistUpdatePreSale,
  ArtistUpdatePreSaleMutation,
  ArtistUpdatePreSaleMutationVariables,
  ConsoleEventCollectionSelectConnectionFragment,
  ConsoleEventCollectionSelectConnectionFragmentDoc,
  ConsoleEventDetailCollectionBoxFragmentDoc,
  ConsoleEventDetailFragmentDoc,
  ConsoleEventDetailQuery,
  ConsoleEventDetailQueryVariables,
  CurrentUserFragmentDoc,
  EventPreSaleBoxFragment,
  EventPreSaleBoxFragmentDoc,
  Maybe,
  SelectCollectionQuery,
  SelectCollectionQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { useIsInside, useLocationWatcher } from '@/core/native/geolocation'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ eventId: string; tab?: 'presale' | 'store' }>()
  const { showError } = useMessage()
  const [updatedAt] = useState(DateTime.now())

  const { data, loading, fetchMore } = useQuery<
    ConsoleEventDetailQuery,
    ConsoleEventDetailQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.eventId,
      idString: router.query.eventId,
    },
  })

  const [
    getSelectCollection,
    {
      data: selectCollection,
      loading: gettingSelectCollection,
      fetchMore: fetchMoreSelectCollection,
    },
  ] = useLazyQuery<SelectCollectionQuery, SelectCollectionQueryVariables>(
    SELECT_COLLECTION
  )

  const [updatePreSale] = useMutation<
    ArtistUpdatePreSaleMutation,
    ArtistUpdatePreSaleMutationVariables
  >(ArtistUpdatePreSale)

  const [artistAddCollectionsToEvent, { loading: addingCollection }] =
    useMutation<
      ArtistAddCollectionsToEventMutation,
      ArtistAddCollectionsToEventMutationVariables
    >(ARTIST_ADD_COLLECTIONS_TO_EVENT)

  const [artistRemoveCollectionFromEvent, { loading: removingCollection }] =
    useMutation<
      ArtistRemoveCollectionFromEventMutation,
      ArtistRemoveCollectionFromEventMutationVariables
    >(ARTIST_REMOVE_COLLECTIONS_FROM_EVENT)

  const [artistUpdateEvent, { loading: changingEventStatus }] = useMutation<
    ArtistUpdateEventMutation,
    ArtistUpdateEventMutationVariables
  >(ArtistUpdateEvent)

  const [artistDeleteEvent, { loading: deletingEvent }] = useMutation<
    ArtistDeleteEventMutation,
    ArtistDeleteEventMutationVariables
  >(ARTIST_DELETE_EVENT)

  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, unwatch, watch])
  const { isInside } = useIsInside({
    locations: [data?.event?.location ?? null, currentLocation],
    radius: 1000,
  })

  const eventCollections = useMemo(
    () =>
      data?.event.eventCollections.edges
        .filter(({ node }) => node.collection.artistId === user?.uid)
        .map(({ node }) => node) ?? null,
    [data?.event.eventCollections.edges, user?.uid]
  )

  const collections = useMemo<
    Maybe<ConsoleEventCollectionSelectConnectionFragment>
  >(() => {
    if (!selectCollection?.artistCollections.edges) {
      return null
    }
    const artistCollections = selectCollection?.artistCollections.edges.filter(
      ({ node }) =>
        !eventCollections?.find(col => col.id === node.id) && !node?.event?.id
    )
    return { ...selectCollection?.artistCollections, edges: artistCollections }
  }, [eventCollections, selectCollection?.artistCollections])

  return {
    state: {
      eventId: router.query.eventId,
      event: data?.event ?? null,
      loading,
      gpsClientInfo: {
        permission,
        currentLocation,
      },
      tab: router.query.tab === 'store' ? 1 : 0,
      collections: eventCollections,
      artistId: user?.uid ?? '',
      isInside,
      removingCollection,
      changingEventStatus,
      deletingEvent,
      hasEventCollectionNextPage:
        data?.event.eventCollections.pageInfo.hasNextPage ?? false,
      hasPresalesNextPage: data?.artistPreSales.pageInfo.hasNextPage ?? false,
      // 先行販売チケットのデータ
      preSale: data?.artistPreSales,
      updatedAt: data?.event ? updatedAt : '',
      selectCollection: {
        loading: gettingSelectCollection,
        hasNextPage:
          !!selectCollection?.artistCollections?.pageInfo?.hasNextPage,
        addingCollection,
        collections,
      },
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            id: router.query.eventId,
            idString: router.query.eventId,
            eventCollectionPageInput: {
              cursor: data?.event.eventCollections.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onFetchPreSalesMore() {
        await fetchMore({
          variables: {
            id: router.query.eventId,
            idString: router.query.eventId,
            eventPreSalePageInput: {
              cursor: data?.artistPreSales.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onRequestPermission() {
        if (!data?.event?.location?.latitude) return
        await requestPermission().catch(showError)
      },
      async onTogglePreSalePublish(preSale: EventPreSaleBoxFragment) {
        await updatePreSale({
          variables: {
            id: preSale.id,
            inputs: {
              title: preSale.title,
              description: preSale.description,
              itemIds: preSale.itemIds,
              startedAt: preSale.startedAt,
              endedAt: preSale.endedAt,
              note: preSale.note,
              isPublished: !preSale.isPublished,
            },
          },
          optimisticResponse({ id }) {
            return {
              artistUpdatePreSale: {
                id,
                __typename: 'PreSale',
                isPublished: !preSale.isPublished,
              },
            }
          },
        })
      },
      async onFetchMoreSelectCollection() {
        await fetchMoreSelectCollection({
          variables: {
            pageInput: {
              cursor: selectCollection?.artistCollections?.pageInfo?.endCursor,
            },
          },
        })
      },
      async onFetchSelectCollection() {
        await getSelectCollection()
      },
      async onAddCollectionsToEvent(values) {
        await artistAddCollectionsToEvent({
          variables: {
            inputs: { eventId: router.query.eventId, collectionIds: values },
          },
        })
      },
      async onRemoveCollectionFromEvent(values) {
        await artistRemoveCollectionFromEvent({
          variables: {
            inputs: { eventId: router.query.eventId, collectionId: values },
          },
        })
      },
      async onChangeEventStatus(eventStatus) {
        await artistUpdateEvent({
          variables: {
            id: router.query.eventId,
            input: { status: eventStatus },
          },
        })
      },
      async onDeleteEvent() {
        await artistDeleteEvent({ variables: { id: router.query.eventId } })
        router.goBack()
      },
    },
  }
})

const QUERY = gql`
  query ConsoleEventDetail(
    $id: ID!
    $idString: String!
    $eventPreSalePageInput: PageInput
    $eventCollectionPageInput: PageInput
  ) {
    artistPreSales(eventId: $idString, pageInput: $eventPreSalePageInput) {
      edges {
        node {
          ...EventPreSaleBox
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    event(id: $id) {
      ...ConsoleEventDetail
      eventCollections(pageInput: $eventCollectionPageInput) {
        edges {
          node {
            ...ConsoleEventDetailCollectionBox
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ConsoleEventDetailFragmentDoc}
  ${ConsoleEventDetailCollectionBoxFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${EventPreSaleBoxFragmentDoc}
`

const SELECT_COLLECTION = gql`
  query SelectCollection($pageInput: PageInputDate) {
    artistCollections(pageInput: $pageInput) {
      ...ConsoleEventCollectionSelectConnection
    }
  }
  ${ConsoleEventCollectionSelectConnectionFragmentDoc}
`

const ARTIST_ADD_COLLECTIONS_TO_EVENT = gql`
  mutation ArtistAddCollectionsToEvent(
    $inputs: ArtistAddCollecitonToEventInput!
  ) {
    artistAddCollectionsToEvent(inputs: $inputs) {
      id
      eventCollections(pageInput: { limit: 30 }) {
        edges {
          node {
            ...ConsoleEventDetailCollectionBox
          }
        }
      }
    }
  }
  ${ConsoleEventDetailCollectionBoxFragmentDoc}
`

const ARTIST_REMOVE_COLLECTIONS_FROM_EVENT = gql`
  mutation ArtistRemoveCollectionFromEvent(
    $inputs: ArtistRemoveCollectionFromEventInput!
  ) {
    artistRemoveCollectionFromEvent(inputs: $inputs) {
      id
    }
  }
`

const ARTIST_DELETE_EVENT = gql`
  mutation ArtistDeleteEvent($id: ID!) {
    artistDeleteEvent(id: $id) {
      id
    }
  }
`
